import { MouseEvent } from "react";
import { useSelector } from "react-redux";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category } from "constants/events";
import { SnackBarMessages, useSnackBar } from "context/SnackBarContext";
import { Coordinates } from "custom-types/Coordinates";
import { MenuItemCard } from "custom-types/MenuItemCard";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import { getIsValidStreetAddress } from "redux/selectors/location";

import Button from "components/botanic/Button";
import ShopNoResults from "components/brands/ShopNoResults";
import CoinList from "components/CoinList";
import { getMenuItemIndicator } from "components/Dispensary/MenuCard/MenuCard";
import HorizontalCardDeliveryFulfillmentInfo from "components/DispensaryFulfillmentInfo/HorizontalCardDeliveryFulfillmentInfo";
import DispensaryMenuItemCard from "components/DispensaryMenuItemCard/DispensaryMenuItemCard";
import HorizontalCardPickupFulfillmentInfo from "components/DispensaryPickupFulfillmentInfo/HorizontalCardPickupFulfillmentInfo";
import TrackImpression from "components/TrackImpression";

import MenuItemCount from "../DeliveryMenuCarousels/MenuItemCount";
import ShopCoreSort from "../ShopCoreSort";
import { SortState } from "../ShopCoreSort/ShopCoreSort";
import MenuItemFilterSection from "../types/MenuItemFilterSection";
import DeliveryProductLoadingSkeleton from "./DeliveryProductLoadingSkeleton";
import DeliveryProductLoadingSkeletonMobile from "./DeliveryProductLoadingSkeletonMobile";
import { DeliverySponsoredProductResults } from "./DeliverySponsoredProductResults";

const DeliveryProductResults = ({
  className,
  coordinates,
  isPickupFulfillmentMode,
  isDeliveryFulfillmentMode,
  loading,
  menuItems,
  onClickLoadMore,
  onChangeSort,
  onShowAddressCaptureModal,
  selectedSort,
  showSponsoredResults = true,
  totalCount,
  brandId,
  brandName,
  isFollowing,
  showEmptyResultsMessaging,
}: {
  className?: string;
  coordinates: Coordinates;
  isDeliveryFulfillmentMode?: boolean;
  isPickupFulfillmentMode?: boolean;
  loading: boolean;
  menuItems: MenuItemCard[];
  onClickLoadMore: () => Promise<void>;
  onChangeSort: (newVal: SortState | null) => void;
  onShowAddressCaptureModal: () => void;
  selectedSort: SortState | null;
  showSponsoredResults?: boolean;
  totalCount: number;
  brandId?: number;
  brandName?: string;
  isFollowing?: boolean;
  showEmptyResultsMessaging?: boolean;
}) => {
  const { filtersApplied, toggleFilter, selectFilterValues, filterValues } =
    useFilters();
  const { clearAllFilters } = useFilters({ shallowRouting: false });
  const { addSnackBarItem } = useSnackBar();
  const { publishEvent } = useEventTracker();
  const isValidAddress = useSelector(getIsValidStreetAddress);
  const displayShowMoreButton = totalCount > menuItems.length;
  const hasFiltersButNoAddress =
    filtersApplied && !isValidAddress && !isPickupFulfillmentMode;

  const handleCardClick = (event: MouseEvent, menuItem: MenuItemCard) => {
    if (hasFiltersButNoAddress) {
      event?.preventDefault();
      onShowAddressCaptureModal();
    } else {
      publishEvent({
        action: Action.click,
        brandId: menuItem.brand?.id,
        category: Category.deliveryGateFlow,
        dispensaryId: menuItem.dispensary.id,
        label: "menu item offer card",
        menuItemId: menuItem.id,
        productId: menuItem.product?.id,
        strainId: menuItem.strain?.id,
      });
    }
  };

  const handleAddToBagClick = (menuItem: MenuItemCard) => {
    const isDispensaryFiltered = selectFilterValues(
      MenuItemFilterSection.DispensaryName,
    ).includes(`${menuItem.dispensary.name}`);
    if (!isDispensaryFiltered) {
      addSnackBarItem(SnackBarMessages.resultsFilteredToDispensary);
      toggleFilter({
        [`${MenuItemFilterSection.DispensaryName}[]`]: menuItem.dispensary.name,
      });
    }
  };

  return (
    <div className={className} data-testid="shop-product-results">
      <div className="flex justify-between">
        <MenuItemCount menuItemsCount={totalCount} />
        <ShopCoreSort
          selectedSort={selectedSort}
          onChange={
            hasFiltersButNoAddress ? onShowAddressCaptureModal : onChangeSort
          }
          isDeliveryFulfillmentMode={isDeliveryFulfillmentMode}
        />
      </div>
      {!isDeliveryFulfillmentMode && showEmptyResultsMessaging && (
        <h2 className="hidden md:block pb-lg">
          Try ordering for in-store pickup.
        </h2>
      )}
      <hr className="border-light-grey -mx-lg mt-sm mb-xl md:hidden" />

      {showSponsoredResults &&
        selectedSort === null &&
        isDeliveryFulfillmentMode &&
        !Object.keys(filterValues).includes("dispensary_name") && (
          <DeliverySponsoredProductResults
            onShowAddressCaptureModal={onShowAddressCaptureModal}
            isDeliveryFulfillmentMode={isDeliveryFulfillmentMode}
            coordinates={coordinates}
            className="-mt-sm md:mt-0"
          />
        )}

      <div className="grid grid-cols-1 mb-xxl mt-sm gap-md">
        {loading ? (
          <>
            <div className="hidden md:block">
              <DeliveryProductLoadingSkeleton />
            </div>
            <div className="block md:hidden">
              <DeliveryProductLoadingSkeletonMobile />
            </div>
          </>
        ) : menuItems?.length ? (
          menuItems.map((menuItem) => (
            <TrackImpression
              key={menuItem.id}
              className="h-full"
              trackingFunction={() =>
                publishEvent({
                  action: Action.impression,
                  brandId: menuItem.brand?.id,
                  category: Category.deliveryGateFlow,
                  dispensaryId: menuItem.dispensary.id,
                  label: "menu item offer card",
                  menuItemId: menuItem.id,
                  productId: menuItem.product?.id,
                  strainId: menuItem.strain?.id,
                })
              }
            >
              <DispensaryMenuItemCard
                menuItem={menuItem}
                menuCardIndicators={getMenuItemIndicator(menuItem)}
                handleCardClick={(e) => {
                  handleCardClick(e, menuItem);
                }}
                useAlternateAddToBagAction={hasFiltersButNoAddress}
                onAlternateAddToBagClick={onShowAddressCaptureModal}
                renderFulfillmentInfo={(dispensary) =>
                  isDeliveryFulfillmentMode ? (
                    <HorizontalCardDeliveryFulfillmentInfo
                      dispensary={dispensary}
                    />
                  ) : (
                    <HorizontalCardPickupFulfillmentInfo
                      dispensary={dispensary}
                    />
                  )
                }
                onReservation={() => {
                  handleAddToBagClick(menuItem);
                }}
              />
            </TrackImpression>
          ))
        ) : (
          <>
            {filtersApplied ? (
              <ShopNoResults
                onButtonClick={() => clearAllFilters()}
                subtitleText="Try selecting fewer options or reset all filters"
                buttonText="reset filters"
                titleText="No results found"
              />
            ) : brandId ? (
              <ShopNoResults
                brandId={brandId}
                {...(isFollowing && { isFollowing })}
                linkText="browse all products"
                linkHref="/shop"
                titleText={`No ${brandName} products found near you`}
                subtitleText={`Follow ${brandName} or browse all available products`}
                buttonText={`follow ${brandName}`}
              />
            ) : (
              <ShopNoResults
                titleText="No matching results"
                buttonText="shop all products"
                onButtonClick={() => clearAllFilters()}
                wrapperClassName="pb-md"
              />
            )}
          </>
        )}
        {menuItems?.length > 0 && (
          <CoinList
            coins={Object.keys(CATEGORY_NAMES)}
            hrefLink="/delivery"
            hrefProductCatQueryKey="product_category[]"
            title="Shop by category"
          />
        )}
      </div>
      {displayShowMoreButton ? (
        <Button
          variant="tertiary"
          className="mb-xxl text-sm"
          onClick={onClickLoadMore}
        >
          show more results
        </Button>
      ) : null}
    </div>
  );
};
export default DeliveryProductResults;
